// Metas mensais da Equipe de Compras (PDF "Painel de Metas — Compras", ciclo Mai/Jun/Jul 2026).
// Núcleo puro e testável: alvos por mês, classificação de faixa (Verde/Amarelo/Vermelho),
// cálculo de OTD e montagem dos 6 indicadores a partir de dados ao vivo + entradas manuais.

export type Faixa = 'verde' | 'amarelo' | 'vermelho'
export type MetaSentido = 'min' | 'max' // min = quanto maior melhor (≥); max = quanto menor melhor (≤)
export type MetaFonte = 'auto' | 'manual'

/** Meta de encalhe por mês — ciclo de redução Mai 15% → Jun 13% → Jul 11%.
 *  Fora do ciclo usa 15% como referência (mesmo patamar de maio). */
export function encalheMetaForMonth(month: number): number {
  switch (month) {
    case 6: return 0.13
    case 7: return 0.11
    default: return 0.15
  }
}

/** Faixa para indicadores "quanto maior, melhor" (≥). */
export function faixaMin(v: number | null, verde: number, amarelo: number): Faixa | null {
  if (v == null) return null
  if (v >= verde) return 'verde'
  if (v >= amarelo) return 'amarelo'
  return 'vermelho'
}

/** Faixa para indicadores "quanto menor, melhor" (≤). */
export function faixaMax(v: number | null, verde: number, amarelo: number): Faixa | null {
  if (v == null) return null
  if (v <= verde) return 'verde'
  if (v <= amarelo) return 'amarelo'
  return 'vermelho'
}

/** Atingiu a meta? Respeita o sentido do indicador. */
export function atingiuMeta(v: number | null, meta: number, sentido: MetaSentido): boolean | null {
  if (v == null) return null
  return sentido === 'min' ? v >= meta : v <= meta
}

// ── OTD (Lead Time): pedidos recebidos no prazo ÷ total recebido ──
export interface OtdPo {
  status: string
  sent_at: string | null
  received_at: string | null
  estimated_arrival: string | null
  lead_time_days: number | null
}
export interface OtdResult { onTime: number; total: number; pct: number | null }

const DAY_MS = 86_400_000

/** OTD sobre POs recebidas. `range` (mês-calendário) recorta por data de recebimento.
 *  "No prazo" = recebido até a previsão (estimated_arrival, ou sent_at + lead_time_days).
 *  Obs.: o critério "completo" (tolerância de quantidade) não é avaliado — não há
 *  quantidade recebida registrada nos itens de PO. */
export function computeOtd(orders: OtdPo[], range?: { start: string; end: string }): OtdResult {
  const received = orders.filter(o => o.status === 'Recebido' && o.sent_at && o.received_at)
  const startMs = range ? new Date(range.start + 'T00:00:00').getTime() : null
  const endMs = range ? new Date(range.end + 'T23:59:59').getTime() : null

  let onTime = 0
  let total = 0
  for (const po of received) {
    const recMs = new Date(po.received_at!).getTime()
    if (startMs != null && endMs != null && (recMs < startMs || recMs > endMs)) continue
    total++
    let dueMs: number | null = null
    if (po.estimated_arrival) dueMs = new Date(po.estimated_arrival).getTime()
    else if (po.sent_at && po.lead_time_days != null) dueMs = new Date(po.sent_at).getTime() + po.lead_time_days * DAY_MS
    if (dueMs != null && recMs <= dueMs) onTime++
  }
  return { onTime, total, pct: total > 0 ? onTime / total : null }
}

// ── Montagem dos 6 indicadores ──
export interface MetasInput {
  totalSkus: number
  consignadoCount: number | null
  rupturaCount: number
  encalheIndex: number | null // fração já calculada (índice geral de encalhe do dashboard)
  otd: number | null          // fração
  crmPct: number | null       // manual: fração
  degustacoes: number | null  // manual: R$ no mês
  month: number               // 1–12 (define a meta de encalhe)
}

export interface IndicadorMeta {
  id: string
  nome: string
  unidade: 'pct' | 'brl'
  sentido: MetaSentido
  meta: number
  metaLabel: string
  value: number | null
  faixa: Faixa | null
  atingiu: boolean | null
  fonte: MetaFonte
  formula: string
}

export function buildMetas(input: MetasInput): IndicadorMeta[] {
  const { totalSkus, consignadoCount, rupturaCount, encalheIndex, otd, crmPct, degustacoes, month } = input

  const consignado = totalSkus > 0 && consignadoCount != null ? consignadoCount / totalSkus : null
  const ruptura = totalSkus > 0 ? rupturaCount / totalSkus : null

  return assembleMetas({ consignado, crm: crmPct, ruptura, encalhe: encalheIndex, otd, degustacoes }, month)
}

/** Valores finais de cada indicador (fração para %, R$ para degustações; null = sem dado).
 *  Usado tanto pelo cálculo ao vivo quanto pelo histórico/snapshots. */
export interface MetaValues {
  consignado: number | null
  crm: number | null
  ruptura: number | null
  encalhe: number | null
  otd: number | null
  degustacoes: number | null
}

/** Monta os 6 indicadores (meta, faixa, status) a partir dos valores já calculados. */
export function assembleMetas(v: MetaValues, month: number): IndicadorMeta[] {
  const encalheMeta = encalheMetaForMonth(month)

  return [
    {
      id: 'consignado', nome: 'Índice de Consignado', unidade: 'pct', sentido: 'min',
      meta: 0.20, metaLabel: '≥ 20%', value: v.consignado,
      faixa: faixaMin(v.consignado, 0.22, 0.20),
      atingiu: atingiuMeta(v.consignado, 0.20, 'min'), fonte: 'auto',
      formula: 'SKUs consignados ÷ SKUs ativos',
    },
    {
      id: 'crm', nome: 'CRM de Fornecedores', unidade: 'pct', sentido: 'min',
      meta: 0.95, metaLabel: '≥ 95%', value: v.crm,
      faixa: faixaMin(v.crm, 0.97, 0.95),
      atingiu: atingiuMeta(v.crm, 0.95, 'min'), fonte: 'manual',
      formula: 'Fornecedores completos e revisados (90d) ÷ total ativos',
    },
    {
      id: 'ruptura', nome: 'Índice de Ruptura', unidade: 'pct', sentido: 'max',
      meta: 0.07, metaLabel: '≤ 7%', value: v.ruptura,
      faixa: faixaMax(v.ruptura, 0.05, 0.07),
      atingiu: atingiuMeta(v.ruptura, 0.07, 'max'), fonte: 'auto',
      formula: 'SKUs em ruptura ÷ SKUs ativos',
    },
    {
      id: 'encalhe', nome: 'Índice de Encalhe', unidade: 'pct', sentido: 'max',
      meta: encalheMeta, metaLabel: `≤ ${Math.round(encalheMeta * 100)}%`, value: v.encalhe,
      faixa: v.encalhe == null ? null : (v.encalhe <= encalheMeta ? 'verde' : 'vermelho'),
      atingiu: atingiuMeta(v.encalhe, encalheMeta, 'max'), fonte: 'auto',
      formula: 'Índice geral de encalhe (ativos + inativos)',
    },
    {
      id: 'otd', nome: 'Lead Time / OTD', unidade: 'pct', sentido: 'min',
      meta: 0.90, metaLabel: '≥ 90%', value: v.otd,
      faixa: faixaMin(v.otd, 0.92, 0.88),
      atingiu: atingiuMeta(v.otd, 0.90, 'min'), fonte: 'auto',
      formula: 'Pedidos recebidos no prazo ÷ total recebido',
    },
    {
      id: 'degustacoes', nome: 'Degustações Mensais', unidade: 'brl', sentido: 'min',
      meta: 1000, metaLabel: '≥ R$ 1.000', value: v.degustacoes,
      faixa: faixaMin(v.degustacoes, 1000, 900),
      atingiu: atingiuMeta(v.degustacoes, 1000, 'min'), fonte: 'manual',
      formula: 'Σ custo unitário × quantidade dos itens em degustação no mês',
    },
  ]
}

/** Extrai os valores finais de um conjunto de indicadores (para salvar como snapshot). */
export function metasToValues(metas: IndicadorMeta[]): MetaValues {
  const by = new Map(metas.map(m => [m.id, m.value]))
  return {
    consignado: by.get('consignado') ?? null,
    crm: by.get('crm') ?? null,
    ruptura: by.get('ruptura') ?? null,
    encalhe: by.get('encalhe') ?? null,
    otd: by.get('otd') ?? null,
    degustacoes: by.get('degustacoes') ?? null,
  }
}

/** Quantas metas foram atingidas (ignora as ainda sem dado). */
export function contarAtingidas(metas: IndicadorMeta[]): { atingidas: number; avaliadas: number } {
  let atingidas = 0
  let avaliadas = 0
  for (const m of metas) {
    if (m.atingiu == null) continue
    avaliadas++
    if (m.atingiu) atingidas++
  }
  return { atingidas, avaliadas }
}
